/**
 * 世界模拟器模板解析服务
 * 负责解析、验证和处理世界模板JSON文件
 */

class WorldTemplateService {
  /**
   * 解析模板JSON
   * @param {string|object} template - JSON字符串或对象
   * @returns {object} 解析后的模板数据
   */
  parseTemplate(template) {
    try {
      const data = typeof template === 'string' ? JSON.parse(template) : template
      
      // 验证必需字段
      this.validateTemplate(data)
      
      // 转换为简化结构
      return this.convertToSimplifiedStructure(data)
    } catch (error) {
      console.error('模板解析失败:', error)
      throw new Error(`模板解析失败: ${error.message}`)
    }
  }

  /**
   * 验证模板格式
   * @param {object} data - 模板数据
   */
  validateTemplate(data) {
    const requiredFields = ['name']
    
    for (const field of requiredFields) {
      if (!data[field]) {
        throw new Error(`缺少必需字段: ${field}`)
      }
    }
    
    // 验证worldBook格式
    if (data.world_book && !Array.isArray(data.world_book)) {
      throw new Error('world_book 必须是数组')
    }
    
    return true
  }

  /**
   * 转换为简化结构
   * @param {object} data - 原始模板数据
   * @returns {object} 简化后的结构
   */
  convertToSimplifiedStructure(data) {
    return {
      name: data.name,
      description: data.summary || this.extractTextFromHTML(data.description) || '',
      summary: data.summary || '',
      coverImage: data.cover || '',
      
      // 核心模板字段
      preText: data.pre_text || '',
      postText: data.post_text || '', // 完整保留，包括NSFW内容
      prePrompt: data.pre_prompt || '',
      
      // 知识库（简化key_region和value_region字段）
      worldBook: (data.world_book || []).map(item => ({
        key: item.key,
        value: item.value,
        group: item.group || '其他'
      })),
      
      // 系统设定（从pre_text中提取或设为默认）
      systems: this.extractSystems(data.pre_text),
      
      // 规则（从pre_text中提取或设为默认）
      rules: this.extractRules(data.pre_text),
      
      // 其他字段
      language: data.language || 'zh-Hans',
      tags: this.extractTags(data),
      type: 'template-imported'
    }
  }

  /**
   * 从HTML描述中提取纯文本
   * @param {string} html - HTML字符串
   * @returns {string} 纯文本
   */
  extractTextFromHTML(html) {
    if (!html) return ''
    
    // 简单的HTML标签移除（前端环境可使用DOMParser）
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = html
    const text = tempDiv.textContent || tempDiv.innerText || ''
    
    // 取前500字符作为描述
    return text.substring(0, 500).trim()
  }

  /**
   * 从preText中提取系统设定
   * @param {string} preText - 前置文本
   * @returns {object} 系统设定
   */
  extractSystems(preText) {
    if (!preText) return {}
    
    const systems = {
      reputation: preText.includes('声望系统') || preText.includes('reputation'),
      resources: preText.includes('资源管理') || preText.includes('resource'),
      events: preText.includes('事件系统') || preText.includes('event'),
      rank: preText.includes('官职') || preText.includes('身份晋升') || preText.includes('rank'),
      morale: preText.includes('民心系统') || preText.includes('morale')
    }
    
    return systems
  }

  /**
   * 从preText中提取规则
   * @param {string} preText - 前置文本
   * @returns {array} 规则数组
   */
  extractRules(preText) {
    if (!preText) return []
    
    const rules = []
    
    // 尝试从markdown格式的规则部分提取
    const rulesMatch = preText.match(/##?\s*规则[\s\S]*?(?=\n##|\n#|$)/i)
    if (rulesMatch) {
      const rulesText = rulesMatch[0]
      const ruleItems = rulesText.match(/[-*]\s*\*\*(.+?)\*\*[：:]\s*(.+?)(?=\n[-*]|\n\n|$)/gs)
      
      if (ruleItems) {
        ruleItems.forEach(item => {
          const match = item.match(/[-*]\s*\*\*(.+?)\*\*[：:]\s*(.+)/)
          if (match) {
            rules.push({
              name: match[1].trim(),
              description: match[2].trim()
            })
          }
        })
      }
    }
    
    return rules
  }

  /**
   * 提取标签
   * @param {object} data - 模板数据
   * @returns {array} 标签数组
   */
  extractTags(data) {
    const tags = []
    
    // 从名称和描述中提取关键词
    const text = `${data.name} ${data.summary || ''}`
    
    // 常见标签匹配
    const tagPatterns = {
      '历史': /明末|清初|民国|三国|战国|历史/,
      '玄幻': /修仙|仙侠|玄幻|灵气/,
      '科幻': /科幻|星际|未来|赛博/,
      '末世': /末世|末日|丧尸|废土/,
      '权谋': /权谋|宫斗|朝堂|政治/,
      '战争': /战争|军事|战场|征战/,
      '江湖': /江湖|武侠|侠客|武林/,
      '商业': /商业|经商|贸易|商人/
    }
    
    for (const [tag, pattern] of Object.entries(tagPatterns)) {
      if (pattern.test(text)) {
        tags.push(tag)
      }
    }
    
    return tags.length > 0 ? tags : ['自定义']
  }

  /**
   * 导出为JSON格式
   * @param {object} worldData - 世界数据
   * @returns {string} JSON字符串
   */
  exportToJSON(worldData) {
    const exportData = {
      name: worldData.name,
      description: worldData.summary || worldData.description,
      summary: worldData.summary,
      language: worldData.language || 'zh-Hans',
      
      // 核心字段
      pre_text: worldData.preText,
      post_text: worldData.postText,
      pre_prompt: worldData.prePrompt,
      
      // 知识库
      world_book: worldData.worldBook.map(item => ({
        key: item.key,
        value: item.value,
        group: item.group,
        key_region: 6,
        value_region: 1
      })),
      
      cover: worldData.coverImage,
      is_anonymous: false,
      banned_words: [],
      cg_book: [],
      opening_statement: worldData.openingStatement || '选择一段开场白',
      suggested_questions: [],
      suggested_questions_after_answer: { enabled: false },
      bg_image: '',
      bg_mobile: '',
      category: 0,
      builtInCss: '',
      mod_permission: 4,
      shortcut_commands: [],
      is_available_not_public: true,
      preset_type: 1,
      preset_chats: []
    }
    
    return JSON.stringify(exportData, null, 2)
  }

  /**
   * 获取示例模板
   * @returns {object} 示例模板数据
   */
  getExampleTemplate() {
    return {
      name: '示例世界',
      description: '这是一个示例世界模板',
      summary: '通过这个示例了解如何创建世界模拟器',
      coverImage: '',
      
      preText: `# 世界观
这是一个示例世界的世界观描述。

## 背景设定
详细的背景设定...

## 地区
### 主城
主城的描述...

## 系统
- **声望系统**：玩家在不同势力中的声望值
- **资源管理**：包括金钱、物资等资源

## 规则
- **真实性**：所有事件符合世界观设定
- **资源稀缺**：资源获取需要努力`,

      postText: `# 核心AI指令
你是这个世界的叙事引擎，负责创造沉浸式体验。

## 叙事原则
1. 保持角色一致性
2. 尊重玩家选择
3. 创造有意义的互动

## 输出要求
- 使用生动的语言
- 注重细节描写
- 提供合理的选项`,

      prePrompt: `# 输出格式
每次回复按以下格式：

\`\`\`
时间：{时间}
地点：{地点}
\`\`\`

{详细的场景描述和对话}

\`\`\`
选项：
A. {选项A}
B. {选项B}
C. {选项C}
\`\`\``,

      worldBook: [
        {
          key: '主角',
          value: '玩家扮演的角色，身份和背景由玩家定义',
          group: '人物'
        }
      ],
      
      systems: {
        reputation: true,
        resources: true,
        events: true,
        rank: false,
        morale: false
      },
      
      rules: [
        {
          name: '真实性',
          description: '所有事件必须符合世界观设定'
        },
        {
          name: '资源稀缺',
          description: '资源获取需要玩家努力'
        }
      ],
      
      language: 'zh-Hans',
      tags: ['示例', '教程'],
      type: 'example'
    }
  }

  /**
   * 验证worldBook条目
   * @param {object} entry - worldBook条目
   * @returns {boolean} 是否有效
   */
  validateWorldBookEntry(entry) {
    return entry && entry.key && entry.value && entry.group
  }

  /**
   * 合并模板数据
   * @param {object} target - 目标数据
   * @param {object} source - 源数据
   * @returns {object} 合并后的数据
   */
  mergeTemplateData(target, source) {
    return {
      ...target,
      ...source,
      worldBook: [
        ...(target.worldBook || []),
        ...(source.worldBook || [])
      ].filter((entry, index, self) => 
        index === self.findIndex(e => e.key === entry.key)
      ),
      systems: {
        ...(target.systems || {}),
        ...(source.systems || {})
      },
      rules: [
        ...(target.rules || []),
        ...(source.rules || [])
      ]
    }
  }

  /**
   * 智能导入JSON - 增强版，识别所有内容
   * @param {string} jsonContent - JSON字符串
   * @param {function} progressCallback - 进度回调函数
   * @returns {object} 包含解析数据和进度信息
   */
  importFromJSON(jsonContent, progressCallback = null) {
    const progress = {
      total: 6,
      current: 0,
      steps: [],
      completedFields: new Set()
    }

    const updateProgress = (step, message, field = null) => {
      progress.current++
      progress.steps.push({ step, message, completed: true })
      if (field) progress.completedFields.add(field)
      if (progressCallback) {
        progressCallback({ ...progress, percentage: Math.round((progress.current / progress.total) * 100) })
      }
    }

    try {
      const data = typeof jsonContent === 'string' ? JSON.parse(jsonContent) : jsonContent
      
      console.log('📖 开始解析JSON，字段数:', Object.keys(data).length)
      
      // 步骤1：提取基本信息
      updateProgress(1, '正在提取基本信息...', 'basic')
      const basicInfo = this.extractBasicInfo(data)
      console.log('✓ 基本信息:', basicInfo.name)
      
      // 步骤2：提取世界观设定
      updateProgress(2, '正在分析世界观设定...', 'worldview')
      const worldviewInfo = this.extractWorldview(data)
      console.log('✓ 世界观:', worldviewInfo.preText ? `${worldviewInfo.preText.length}字` : '无', 
                  'worldBook:', worldviewInfo.worldBook?.length || 0, '条')
      
      // 步骤3：提取系统和规则
      updateProgress(3, '正在识别系统规则...', 'systems')
      const systemsInfo = this.extractSystemsAndRules(data)
      console.log('✓ 系统:', Object.keys(systemsInfo.systems).filter(k => systemsInfo.systems[k]).length, '个',
                  '规则:', systemsInfo.rules?.length || 0, '条')
      
      // 步骤4：提取地区信息
      updateProgress(4, '正在提取地区设定...', 'regions')
      const regionsInfo = this.extractRegions(data)
      console.log('✓ 地区:', regionsInfo.regions?.length || 0, '个')
      
      // 步骤5：提取角色信息
      updateProgress(5, '正在提取角色设定...', 'characters')
      const charactersInfo = this.extractCharacters(data)
      console.log('✓ 角色:', charactersInfo.characters?.length || 0, '个')
      
      // 步骤6：整合所有信息
      updateProgress(6, '正在整合所有信息...', 'confirm')
      const worldData = {
        ...basicInfo,
        ...worldviewInfo,
        ...systemsInfo,
        ...regionsInfo,
        ...charactersInfo,
        type: 'imported',
        importTime: Date.now()
      }
      
      console.log('📦 整合完成，总字段数:', Object.keys(worldData).length)
      
      const completeness = this.calculateCompleteness(worldData)
      console.log('📊 完整度检查:', completeness)
      
      return {
        data: worldData,
        progress: progress,
        completeness: completeness
      }
      
    } catch (error) {
      console.error('JSON导入失败:', error)
      throw new Error(`导入失败: ${error.message}`)
    }
  }

  /**
   * 提取基本信息
   */
  extractBasicInfo(data) {
    // 处理description - 如果是HTML，提取文本
    let description = data.summary || data.brief || ''
    
    if (!description && data.description) {
      // 如果description是HTML，提取纯文本
      description = this.extractTextFromHTML(data.description)
    }
    
    return {
      name: data.name || data.title || data.worldName || '未命名世界',
      description: description,
      summary: data.summary || data.brief || description.substring(0, 500) || '',
      coverImage: data.cover || data.cover_tiny || data.coverImage || data.image || data.avatar || '',
      language: data.language || data.lang || 'zh-Hans'
    }
  }

  /**
   * 提取世界观设定
   */
  extractWorldview(data) {
    let preText = data.pre_text || data.preText || data.worldview || data.setting || ''
    
    // 构建world_book数据
    let worldBook = []
    if (data.world_book && Array.isArray(data.world_book)) {
      worldBook = data.world_book.map(item => ({
        key: item.key || '',
        value: item.value || '',
        group: item.group || '其他'
      }))
      
      // 如果preText为空，尝试从world_book中提取世界观相关内容
      if (!preText) {
        const worldviewEntries = data.world_book.filter(item => 
          item.group === '世界观' || 
          item.group === '设定' ||
          (item.key && (item.key.includes('世界') || item.key.includes('背景')))
        )
        
        if (worldviewEntries.length > 0) {
          preText = worldviewEntries.map(e => `## ${e.key}\n${e.value}`).join('\n\n')
        }
      }
    }
    
    // 尝试从其他字段提取
    if (!preText) {
      const possibleFields = [
        data.background, 
        data.lore, 
        data.backstory,
        data.world_setting,
        data.scenario
      ].filter(Boolean)
      
      if (possibleFields.length > 0) {
        preText = possibleFields.join('\n\n')
      }
    }
    
    return {
      preText,
      postText: data.post_text || data.postText || data.aiInstructions || '',
      prePrompt: data.pre_prompt || data.prePrompt || data.formatInstructions || '',
      worldBook // 添加worldBook到返回值
    }
  }

  /**
   * 提取系统和规则
   */
  extractSystemsAndRules(data) {
    // 提取系统
    let systems = data.systems || {}
    
    // 如果没有系统定义，尝试从preText中提取
    if (Object.keys(systems).length === 0 && data.pre_text) {
      systems = this.extractSystems(data.pre_text)
    }
    
    // 尝试从world_book中识别系统
    if (data.world_book && Array.isArray(data.world_book)) {
      const systemEntries = data.world_book.filter(item => 
        item.group === '系统' || 
        item.key.includes('系统') ||
        item.key.includes('规则')
      )
      
      systemEntries.forEach(entry => {
        const key = entry.key.toLowerCase()
        if (key.includes('声望') || key.includes('reputation')) systems.reputation = true
        if (key.includes('资源') || key.includes('resource')) systems.resources = true
        if (key.includes('事件') || key.includes('event')) systems.events = true
        if (key.includes('官职') || key.includes('rank')) systems.rank = true
        if (key.includes('民心') || key.includes('morale')) systems.morale = true
      })
    }
    
    // 提取规则
    let rules = data.rules || []
    if (rules.length === 0 && data.pre_text) {
      rules = this.extractRules(data.pre_text)
    }
    
    // 从world_book中提取规则
    if (data.world_book && Array.isArray(data.world_book)) {
      const ruleEntries = data.world_book.filter(item => 
        item.group === '规则' || 
        item.key.includes('规则') ||
        item.key.includes('原则')
      )
      
      ruleEntries.forEach(entry => {
        rules.push({
          name: entry.key,
          description: entry.value
        })
      })
    }
    
    // 提取量化系统
    let quantitativeSystems = data.quantitativeSystems || data.quantitative_systems || []
    
    // 尝试从world_book中识别量化系统
    if (data.world_book && Array.isArray(data.world_book)) {
      const quantEntries = data.world_book.filter(item => 
        item.group === '数值' || 
        item.group === '属性' ||
        item.key.includes('属性') ||
        item.key.includes('数值')
      )
      
      quantEntries.forEach(entry => {
        quantitativeSystems.push({
          name: entry.key,
          description: entry.value,
          min: 0,
          max: 100
        })
      })
    }
    
    return { systems, rules, quantitativeSystems }
  }

  /**
   * 提取地区信息
   */
  extractRegions(data) {
    let regions = data.regions || data.locations || data.places || []
    
    // 从world_book中提取地区
    if (data.world_book && Array.isArray(data.world_book)) {
      const regionEntries = data.world_book.filter(item => 
        item.group === '地理与区域' ||
        item.group === '地区' || 
        item.group === '地点' ||
        item.group === '场所' ||
        (item.key && (
          item.key.includes('地区') ||
          item.key.includes('城市') ||
          item.key.includes('区域') ||
          item.key.includes('京城') ||
          item.key.includes('辽东') ||
          item.key.includes('江南') ||
          item.key.includes('陕西')
        ))
      )
      
      regionEntries.forEach(entry => {
        // 清理key中的特殊字符
        let cleanKey = entry.key.replace(/_or_/g, '').split('@wb@')[0].trim()
        
        regions.push({
          name: cleanKey,
          description: entry.value,
          type: this.detectRegionType(cleanKey, entry.value)
        })
      })
    }
    
    // 尝试从pre_text中提取地区
    if (regions.length === 0 && data.pre_text) {
      const regionMatches = data.pre_text.match(/###?\s*(.+?)\n([\s\S]{50,500}?)(?=\n###|\n##|$)/g)
      if (regionMatches) {
        regionMatches.forEach(match => {
          const lines = match.split('\n')
          const name = lines[0].replace(/^###?\s*/, '').trim()
          const description = lines.slice(1).join('\n').trim()
          
          // 检查是否看起来像地区描述
          if (description.length > 30 && (
            name.includes('城') || name.includes('省') || name.includes('地区') ||
            name.includes('京') || name.includes('江南') || name.includes('辽东')
          )) {
            regions.push({
              name,
              description,
              type: this.detectRegionType(name, description)
            })
          }
        })
      }
    }
    
    // 尝试从其他字段提取
    if (regions.length === 0) {
      const possibleFields = [
        data.map,
        data.geography,
        data.areas
      ].filter(Boolean)
      
      if (possibleFields.length > 0) {
        // 简单解析：每个段落作为一个地区
        possibleFields.forEach(field => {
          const lines = field.split('\n').filter(line => line.trim())
          lines.forEach(line => {
            if (line.length > 10 && line.length < 200) {
              const parts = line.split(/[:：]/)
              if (parts.length >= 2) {
                regions.push({
                  name: parts[0].trim(),
                  description: parts.slice(1).join(':').trim(),
                  type: '其他'
                })
              }
            }
          })
        })
      }
    }
    
    return { regions }
  }

  /**
   * 提取角色信息
   */
  extractCharacters(data) {
    let characters = data.characters || data.npcs || data.persons || []
    
    // 从world_book中提取角色
    if (data.world_book && Array.isArray(data.world_book)) {
      const characterEntries = data.world_book.filter(item => 
        item.group === '人物' || 
        item.group === '角色' ||
        item.group === 'NPC' ||
        (item.key && (
          item.key.includes('人物') ||
          item.key.includes('角色')
        ))
      )
      
      characterEntries.forEach(entry => {
        // 清理key中的特殊字符，提取主要名字
        let cleanKey = entry.key.replace(/_or_/g, '').split('@wb@')[0].trim()
        
        characters.push({
          name: cleanKey,
          description: entry.value,
          role: this.detectCharacterRole(entry.value),
          avatar: ''
        })
      })
    }
    
    return { characters }
  }

  /**
   * 检测地区类型
   */
  detectRegionType(name, description) {
    const text = `${name} ${description}`.toLowerCase()
    
    if (text.includes('城市') || text.includes('城镇') || text.includes('city')) return '城市'
    if (text.includes('村庄') || text.includes('村落') || text.includes('village')) return '村庄'
    if (text.includes('山') || text.includes('mountain')) return '自然'
    if (text.includes('森林') || text.includes('forest')) return '自然'
    if (text.includes('海') || text.includes('ocean') || text.includes('sea')) return '自然'
    if (text.includes('宫殿') || text.includes('宫廷') || text.includes('palace')) return '建筑'
    if (text.includes('寺庙') || text.includes('temple')) return '建筑'
    if (text.includes('商会') || text.includes('市场') || text.includes('market')) return '商业'
    
    return '其他'
  }

  /**
   * 检测角色角色
   */
  detectCharacterRole(description) {
    const text = description.toLowerCase()
    
    if (text.includes('皇帝') || text.includes('国王') || text.includes('emperor') || text.includes('king')) return '统治者'
    if (text.includes('将军') || text.includes('general')) return '军事'
    if (text.includes('商人') || text.includes('merchant')) return '商人'
    if (text.includes('学者') || text.includes('scholar')) return '学者'
    if (text.includes('侠客') || text.includes('武者')) return '武者'
    if (text.includes('僧侣') || text.includes('道士')) return '宗教'
    
    return '其他'
  }

  /**
   * 判断是否像人名
   */
  isLikelyCharacterName(text) {
    // 中文人名通常2-4个字
    if (/^[\u4e00-\u9fa5]{2,4}$/.test(text)) {
      // 排除一些明显不是人名的词
      const excluded = ['世界', '系统', '规则', '地区', '城市', '村庄', '设定', '背景']
      return !excluded.some(word => text.includes(word))
    }
    
    // 英文人名通常是首字母大写的单词或两个单词
    if (/^[A-Z][a-z]+(\s[A-Z][a-z]+)?$/.test(text)) {
      return true
    }
    
    return false
  }

  /**
   * 计算完整度
   */
  calculateCompleteness(data) {
    const checks = {
      basic: !!(data.name && data.description),
      worldview: !!data.preText,
      systems: !!(data.systems && Object.keys(data.systems).length > 0),
      regions: !!(data.regions && data.regions.length >= 2),
      characters: !!(data.characters && data.characters.length >= 2)
    }
    
    const completed = Object.values(checks).filter(Boolean).length
    const total = Object.keys(checks).length
    
    return {
      checks,
      completed,
      total,
      percentage: Math.round((completed / total) * 100),
      isComplete: completed >= 4 // 至少完成4项
    }
  }
}

// 创建单例
const worldTemplateService = new WorldTemplateService()

export default worldTemplateService

